import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "../../../../../../redux";
import { Integration } from "../../../../../../redux/slices/projects/Types";
import OnboardingStep, {
  getOnboardingCurrentStatus,
} from "../integration-steps/onboarding";
import TrainingStep, {
  getTrainingCurrentStatus,
} from "../integration-steps/training";
import DevelopmentStep, {
  getDevelopmentCurrentStatus,
} from "../integration-steps/development";
import ValidationStep, {
  getValidationCurrentStatus,
} from "../integration-steps/validation";
import { IntegrationStatusLabel } from "../integration-steps/shared/StatusTag";
import type { IntegrationPhase } from "./IntegrationTimelineSection";
import PhaseDescriptionModal from "./PhaseDescriptionModal";
import "./IntegrationPhasePanel.scss";

type IntegrationPhasePanelProps = {
  integration: Integration;
  selectedPhase: IntegrationPhase;
};

const PHASE_STATUS_GETTERS: Record<
  IntegrationPhase,
  (integration?: Integration) => IntegrationStatusLabel | null
> = {
  Onboarding: getOnboardingCurrentStatus,
  Training: getTrainingCurrentStatus,
  Development: getDevelopmentCurrentStatus,
  Validation: getValidationCurrentStatus,
};

const VALIDATION_SCORE_CARDS = [
  { key: "mandatory", label: "mandatory" },
  { key: "recommended", label: "recommended" },
  { key: "bestPractice", label: "bestpractice" },
  { key: "optional", label: "optional" },
] as const;

const IntegrationPhasePanel: React.FC<IntegrationPhasePanelProps> = ({
  integration,
  selectedPhase,
}) => {
  const navigate = useNavigate();
  const [isDescriptionOpen, setIsDescriptionOpen] = React.useState(false);
  const integrationSummary = useAppSelector(
    (state) =>
      state.projects.integrationSummaries[integration.integration_id] ??
      integration.summary ??
      null
  );

  const phaseStatuses = React.useMemo(
    () =>
      (Object.keys(PHASE_STATUS_GETTERS) as IntegrationPhase[]).map((phase) => ({
        phase,
        status: PHASE_STATUS_GETTERS[phase](integration),
      })),
    [integration]
  );

  const selectedPhaseIndex = phaseStatuses.findIndex(
    (entry) => entry.phase === selectedPhase
  );

  const isFormDisabled =
    selectedPhaseIndex > 0 &&
    phaseStatuses
      .slice(0, selectedPhaseIndex)
      .some(
        (entry) =>
          entry.status !== "Completed" && entry.status !== "Not applicable"
      );

  const renderPhaseStep = () => {
    switch (selectedPhase) {
      case "Onboarding":
        return (
          <OnboardingStep
            integration={integration}
            isFormDisabled={isFormDisabled}
          />
        );
      case "Training":
        return (
          <TrainingStep
            integration={integration}
            isFormDisabled={isFormDisabled}
          />
        );
      case "Development":
        return (
          <DevelopmentStep
            integration={integration}
            isFormDisabled={isFormDisabled}
          />
        );
      case "Validation":
        return (
          <ValidationStep
            integration={integration}
            isFormDisabled={isFormDisabled}
          />
        );
      default:
        return null;
    }
  };

  const showValidationSummary = selectedPhase === "Validation";

  return (
    <>
      <div className="integration-phase-panel">
        {showValidationSummary && (
          <div className="integration-phase-panel__score-cards">
            {VALIDATION_SCORE_CARDS.map((card) => {
              const scoreValue = integrationSummary?.scores?.[card.key];
              return (
                <div
                  key={card.key}
                  className="integration-phase-panel__score-card"
                >
                  <span className="integration-phase-panel__score-label">
                    {card.label}
                  </span>
                  <span className="integration-phase-panel__score-value">
                    {scoreValue != null ? `${scoreValue}%` : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="integration-phase-panel__meta">
          {showValidationSummary && (
            <div className="integration-phase-panel__meta-left">
              <span>
                Overall Score:{" "}
                <strong>
                  {integrationSummary?.overall_score != null
                    ? integrationSummary.overall_score
                    : "—"}
                     /{integrationSummary?.total_validation_score ?? "-"}
                </strong>
              </span>
              <span className="integration-phase-panel__meta-separator">|</span>
              <span>
                Endorsement Level:{" "}
                <strong>
                  {integrationSummary?.endorsement_level ?? "—"}
                </strong>
              </span>
            </div>
          )}
          <div className="integration-phase-panel__meta-right">
            <button
              type="button"
              className="integration-phase-panel__description-link"
              onClick={() => setIsDescriptionOpen(true)}
            >
              View description
            </button>
            {selectedPhase === "Validation" &&
              getValidationCurrentStatus(integration) !== "Not applicable" && (
                <Button
                  variant="primary"
                  label="View Validation Details"
                  normalCase={true}
                  className="integration-phase-panel__validation-details"
                  onClick={() =>
                    navigate(`validation/${integration.integration_id}`)
                  }
                />
              )}
          </div>
        </div>

        <div className="integration-phase-panel__step-content">
          {renderPhaseStep()}
        </div>
      </div>

      {isDescriptionOpen ? (
        <PhaseDescriptionModal
          selectedPhase={selectedPhase}
          onClose={() => setIsDescriptionOpen(false)}
        />
      ) : null}
    </>
  );
};

export default IntegrationPhasePanel;
